/**
 * **Meta 광고 질문** — 파라미터를 보내고 도메인 타입을 받는다. 질문 3개가 여기 있다.
 *
 * ```
 *   sections/meta/…             취소 · 로딩 (여기 없다)
 *     │ fetchAdsList(params)
 *   이 파일                      질문 — op 이름과 결과 모양을 안다
 *     │ resolveMock*()          mockup 갈래 (actions/meta-ads/meta-ads-mock · 없으면 아래로)
 *     │ els-request.ts          전송 (dedupe · 2초 캐시)
 *     ▼ POST /api/els/ads-meta      { op, params }
 *   server/ads-meta/             답한다
 * ```
 *
 * 규약은 `actions/els/moment.ts`·`product-rankings.ts`와 같다 — op마다 함수 하나,
 * 타입 단언 없음, 닻이 되는 필드만 확인.
 *
 * ## ⚠️ 다중 선택은 **배열로 보낸다**
 *
 * 서버 리더는 `"active,inactive"` 같은 콤마 문자열도 아직 받아 주지만, 새 호출부는
 * 배열만 쓴다.
 *
 * ⚠️ `active`가 **비어 있으면 진행중만** 본다(서버 기본값). 둘 다 고르면 필터가 아예
 * 빠진다 — "아무것도 안 고름"과 "둘 다 고름"이 다른 뜻이다.
 */

import type { ElsProxyCallOptions } from 'src/actions/els/els-request';
import type {
  AdThumb,
  AdStatsRow,
  AdBrandFace,
  AdListRequest,
  AdStatsRequest,
  AdListResponse,
  AdByIdsRequest,
  AdStatsResponse,
  AdDetailRequest,
  AdThumbsRequest,
  AdByIdsResponse,
  AdFacetsResponse,
  AdDetailResponse,
  AdThumbsResponse,
  AdPublisherRequest,
  AdPublisherResponse,
  AdBrandFacesRequest,
  AdStartDailyRequest,
  AdBrandFacesResponse,
  AdStartDailyResponse,
} from 'src/types/ads-meta/ads-meta-ops';

import { createElsProxyRequest } from 'src/actions/els/els-request';
import {
  resolveMockAdsList,
  resolveMockAdsStats,
  resolveMockAdThumbs,
  resolveMockAdsDetail,
  resolveMockAdsFacets,
  resolveMockAdBrandFaces,
  resolveMockAdsByPublisher,
} from 'src/actions/meta-ads/meta-ads-mock';

/** 인덱스당 엔드포인트 하나 */
const ADS_META_OPS_PATH = '/api/els/ads-meta';

const requestAdsOp = createElsProxyRequest(ADS_META_OPS_PATH);

function readOpData(data: unknown, who: string): object {
  if (data == null || typeof data !== 'object') throw new Error(`[${who}] 응답이 객체가 아니다`);
  if ('error' in data) throw new Error(`[${who}] ${String(data.error)}`);
  return data;
}

function readNumber(value: unknown): number {
  return typeof value === 'number' && Number.isFinite(value) ? value : 0;
}

function readText(value: unknown): string {
  return typeof value === 'string' ? value : '';
}

function readList<T>(value: unknown): T[] {
  return Array.isArray(value) ? value : [];
}

function readObject(value: unknown): Record<string, never> {
  return value != null && typeof value === 'object' ? (value as Record<string, never>) : {};
}

/**
 * 목록 (`ads.list`).
 *
 * ⚠️ `total`과 `hasMore`의 **기준이 다르다.** 묶음이 켜져 있으면 `total`은 묶음 수이고
 * `hasMore`는 문서 수 기준이다 — 묶으면 보이는 개수가 줄어서 그렇다.
 *
 * ⚠️ 묶음 자체는 **화면이 한다.** 서버는 개수만 세어 준다 — 정렬을 ES가 하고 묶음을
 * 화면이 하는 것은 같은 페이지 안에서 순서를 지키기 위해서다.
 */
export async function fetchAdsList(
  params: AdListRequest,
  options: ElsProxyCallOptions = {},
): Promise<AdListResponse> {
  const mock = resolveMockAdsList(params);
  if (mock) return mock;

  const response = await requestAdsOp({ op: 'ads.list', params }, options);
  const data = readOpData(response.data, 'fetchAdsList');

  return {
    items: readList('items' in data ? data.items : null),
    from: readNumber('from' in data ? data.from : null),
    limit: readNumber('limit' in data ? data.limit : null),
    hasMore: ('hasMore' in data ? data.hasMore : null) === true,
    nextFrom: readNumber('nextFrom' in data ? data.nextFrom : null),
    total: readNumber('total' in data ? data.total : null),
    rawTotal: readNumber('rawTotal' in data ? data.rawTotal : null),
    groupBy: readGroupBy('groupBy' in data ? data.groupBy : null),
    latestSeen: readText('latestSeen' in data ? data.latestSeen : null),
  };
}

/**
 * 패싯·counts·최신 수집일 (`ads.facets`).
 *
 * ⚠️ **인자를 받지 않는다** — 필터·페이지와 무관한 전역값이라, 화면은 이 질문을
 * 긴 `staleTime` 으로 한 번만 던진다. `ads.list` 응답에는 실려 오지 않는다.
 */
export async function fetchAdFacets(
  options: ElsProxyCallOptions = {},
): Promise<AdFacetsResponse> {
  const mock = resolveMockAdsFacets();
  if (mock) return mock;

  const response = await requestAdsOp({ op: 'ads.facets', params: {} }, options);
  const data = readOpData(response.data, 'fetchAdFacets');
  const counts = readObject('counts' in data ? data.counts : null);

  return {
    counts: {
      active: readNumber('active' in counts ? counts.active : null),
      inactive: readNumber('inactive' in counts ? counts.inactive : null),
    },
    latestDate: readText('latestDate' in data ? data.latestDate : null),
    facets: readFacets('facets' in data ? data.facets : null),
  };
}

function readGroupBy(value: unknown): AdListResponse['groupBy'] {
  return value === 'image' || value === 'none' ? value : 'text';
}

/**
 * ⚠️ 고정 갈래(`posting` 둘·`posterAI` 넷)는 **0이라도 채운다** — 화면이 칩을 고정으로
 * 그린다. 나머지 버킷은 이름이 데이터에서 오므로 여기서 셀 수 있는 것이 없다.
 *
 * ⚠️ `posting`의 축은 `adDeliveryType`이다 — `posterAI`의 네 갈래와 헷갈리지 마라.
 */
function readFacets(value: unknown): AdFacetsResponse['facets'] {
  const facets = readObject(value);
  const posting = readObject('posting' in facets ? facets.posting : null);
  const posterAI = readObject('posterAI' in facets ? facets.posterAI : null);

  return {
    mediaTypes: readObject('mediaTypes' in facets ? facets.mediaTypes : null),
    posting: {
      direct: readNumber('direct' in posting ? posting.direct : null),
      partnership: readNumber('partnership' in posting ? posting.partnership : null),
    },
    posterAI: {
      'brand-official': readNumber('brand-official' in posterAI ? posterAI['brand-official'] : null),
      creator: readNumber('creator' in posterAI ? posterAI.creator : null),
      reseller: readNumber('reseller' in posterAI ? posterAI.reseller : null),
      other: readNumber('other' in posterAI ? posterAI.other : null),
    },
    landing: readObject('landing' in facets ? facets.landing : null),
    adCountry: readObject('adCountry' in facets ? facets.adCountry : null),
    mentioned: readObject('mentioned' in facets ? facets.mentioned : null),
  };
}

/**
 * **통계** (`ads.stats`) — 목록과 같은 조건으로 축마다 센 숫자. 카드는 오지 않는다.
 */
/**
 * **브랜드의 얼굴** (`ads.brandFaces`) — 그 브랜드로 광고를 돌린 메타 페이지의 프로필 사진.
 *
 * 🔑 자사 미러라 **만료가 없다** — 발굴 원장의 인스타 원본 주소와 다른 점이다.
 * ⚠️ 광고를 안 돌린 브랜드는 목록에 **없다**(부르는 쪽이 머리글자로 떨어뜨린다).
 */
export async function fetchAdBrandFaces(params: AdBrandFacesRequest): Promise<AdBrandFacesResponse> {
  const mock = resolveMockAdBrandFaces(params);
  if (mock) return mock;

  const response = await requestAdsOp({ op: 'ads.brandFaces', params });
  const data = readOpData(response.data, 'fetchAdBrandFaces');
  const raw = 'faces' in data && Array.isArray(data.faces) ? data.faces : [];
  const faces: AdBrandFace[] = [];
  for (const item of raw) {
    const row = (item != null && typeof item === 'object' ? item : {}) as Record<string, unknown>;
    const brand = String(row.brand ?? '').trim();
    const image = String(row.image ?? '').trim();
    if (!brand || !image) continue;
    faces.push({
      brand,
      image,
      pageId: String(row.pageId ?? '').trim(),
      pageName: String(row.pageName ?? '').trim(),
      count: Number(row.count ?? 0) || 0,
    });
  }
  return { faces };
}

export async function fetchAdsStats(params: AdStatsRequest): Promise<AdStatsResponse> {
  const mock = resolveMockAdsStats(params);
  if (mock) return mock;

  const response = await requestAdsOp({ op: 'ads.stats', params });
  const data = readOpData(response.data, 'fetchAdsStats');
  const rows = (key: string): AdStatsRow[] => {
    const value = key in data ? (data as Record<string, unknown>)[key] : null;
    if (!Array.isArray(value)) return [];
    return value
      .map((raw) => (raw != null && typeof raw === 'object' ? raw : {}) as Record<string, unknown>)
      .map((row) => ({ name: String(row.name ?? ''), count: Number(row.count ?? 0) }))
      .filter((row) => row.name !== '');
  };
  const num = (key: string) => Number((key in data ? (data as Record<string, unknown>)[key] : 0) ?? 0) || 0;

  return {
    total: num('total'),
    freshCount: num('freshCount'),
    avgLifespanDays: num('avgLifespanDays'),
    brands: rows('brands'),
    pages: rows('pages'),
    offers: rows('offers'),
    countries: rows('countries'),
    ctas: rows('ctas'),
    formats: rows('formats'),
    landings: rows('landings'),
  };
}

/** **날마다 시작한 광고 소재 수** (`ads.startDaily`) — 날짜(`YYYYMMDD`) → 수 · 최근 시작 브랜드 수 */
export async function fetchAdStartDaily(params: AdStartDailyRequest): Promise<AdStartDailyResponse> {
  const response = await requestAdsOp({ op: 'ads.startDaily', params });
  const data = readOpData(response.data, 'fetchAdStartDaily') as Record<string, unknown>;
  const raw = data.daily != null && typeof data.daily === 'object' ? (data.daily as Record<string, unknown>) : {};
  const daily: Record<string, number> = {};
  for (const [day, count] of Object.entries(raw)) daily[day] = readNumber(count);
  return { daily, total: readNumber(data.total), recentBrands: readNumber(data.recentBrands) };
}

/**
 * 단건 (`ads.detail`).
 *
 * ⚠️ 못 찾으면 세 필드가 전부 `null`이다 — 404가 아니다.
 * ⚠️ `esDoc`은 ES 원본을 통째로 담는다. 화면이 매핑 안 된 필드를 읽는지 확인되기
 * 전까지 줄일 수 없다(설계 미결).
 */
export async function fetchAdDetail(params: AdDetailRequest): Promise<AdDetailResponse> {
  const mock = resolveMockAdsDetail(params);
  if (mock) return mock;

  const response = await requestAdsOp({ op: 'ads.detail', params });
  const data = readOpData(response.data, 'fetchAdDetail');

  const creative = 'creative' in data ? data.creative : null;
  if (creative == null || typeof creative !== 'object') {
    return { creative: null, brand: null, esDoc: null };
  }
  if (!isAdCreative(creative)) {
    throw new Error('[fetchAdDetail] creative 모양이 아니다');
  }

  const brand = 'brand' in data ? data.brand : null;
  return {
    creative,
    brand: brand != null && typeof brand === 'object' ? readBrand(brand) : null,
    esDoc: readEsDoc('esDoc' in data ? data.esDoc : null),
  };
}

/**
 * 광고 여럿의 카드 본문 (`ads.byIds`) — 단건(`fetchAdDetail`)의 `creative` 와 같은 값이고 `esDoc` 은 없다.
 *
 * ⚠️ 답은 묻은 id 와 같은 순서·같은 길이다 — 못 찾은 자리는 `null` · 한 번에 `AD_BY_IDS_MAX` 개까지(부르는 쪽이 나눈다).
 * ⚠️ 모양이 아닌 항목은 `null` 로 읽는다(단건이 그 id 하나만 실패하던 것과 같다).
 * mockup 이 단건 답을 쥔 id 는 그 답을 쓰고, 나머지만 묻는다.
 */
export async function fetchAdsByIds(params: AdByIdsRequest): Promise<AdByIdsResponse> {
  type Slot = { creative: AdByIdsResponse['creatives'][number] } | null;
  const slots: Slot[] = params.adIds.map((adId) => {
    const mock = resolveMockAdsDetail({ adId });
    return mock ? { creative: mock.creative } : null;
  });
  const asked = params.adIds.filter((_, i) => slots[i] == null);
  if (asked.length === 0) return { creatives: slots.map((slot) => slot?.creative ?? null) };

  const response = await requestAdsOp({ op: 'ads.byIds', params: { adIds: asked } });
  const data = readOpData(response.data, 'fetchAdsByIds');
  const raw = 'creatives' in data && Array.isArray(data.creatives) ? data.creatives : [];
  let next = 0;
  return {
    creatives: slots.map((slot) => {
      if (slot) return slot.creative;
      const item: unknown = raw[next++];
      return item != null && typeof item === 'object' && isAdCreative(item) ? item : null;
    }),
  };
}

/**
 * ES 원본을 그대로 통과시킨다.
 *
 * ⚠️ **키를 세지 않는다** — 색인 필드가 전부 들어 있고 화면이 매핑 안 된 것을 읽는지
 * 아직 확인되지 않았다(설계 미결). 확인하는 것은 "객체인가"뿐이다.
 */
function readEsDoc(value: unknown): Record<string, unknown> | null {
  if (value == null || typeof value !== 'object' || Array.isArray(value)) return null;
  const doc: Record<string, unknown> = {};
  for (const [key, item] of Object.entries(value)) doc[key] = item;
  return doc;
}

/** 닻이 되는 필드 셋만 본다 — 50여 필드를 다 세지 않는다 */
function isAdCreative(value: object): value is NonNullable<AdDetailResponse['creative']> {
  return 'id' in value && typeof value.id === 'string'
    && 'media' in value && Array.isArray(value.media)
    && 'status' in value && typeof value.status === 'string';
}

function readBrand(value: object): NonNullable<AdDetailResponse['brand']> {
  const rank = 'rank' in value ? value.rank : null;
  return {
    handle: readText('handle' in value ? value.handle : null),
    brand: readText('brand' in value ? value.brand : null),
    promotedBrand: readText('promotedBrand' in value ? value.promotedBrand : null),
    rank: typeof rank === 'number' && Number.isFinite(rank) ? rank : null,
  };
}

/**
 * **이 계정이 게시자인 Meta 광고** (`ads.byPublisher`) — 「광고로 나간 컨텐츠」.
 *
 * 🔑 크리에이터는 광고 문서의 `pageName`·`pageVanity` 에 있다(협업 광고의 게시자).
 * ⚠️ 브랜드가 크리에이터 영상을 **자기 페이지로** 돌리면 안 걸린다 — 「일부러」 안 센다.
 */
export async function fetchAdsByPublisher(params: AdPublisherRequest): Promise<AdPublisherResponse> {
  const mock = resolveMockAdsByPublisher(params);
  if (mock) return mock;

  const response = await requestAdsOp({ op: 'ads.byPublisher', params });
  const data = readOpData(response.data, 'fetchAdsByPublisher');
  const rawAdvertisers = 'advertisers' in data && Array.isArray(data.advertisers) ? data.advertisers : [];

  return {
    total: readNumber('total' in data ? data.total : null),
    active: readNumber('active' in data ? data.active : null),
    withPreview: readNumber('withPreview' in data ? data.withPreview : null),
    advertisers: rawAdvertisers.map((row) => String(row ?? '').trim()).filter(Boolean),
    items: readList('items' in data ? data.items : null),
  };
}

/**
 * **담아 둔 소재의 얼굴** (`ads.thumbs`) — 광고 id 여럿 → 미리보기 사진 한 장씩.
 *
 * 🔑 단건(`ads.detail`)을 id 수만큼 부르지 않으려고 둔 질문이다.
 * ⚠️ 미러가 없는 소재는 **답에 없다** — 부르는 쪽이 빈 칸으로 둔다.
 */
export async function fetchAdThumbs(params: AdThumbsRequest): Promise<AdThumbsResponse> {
  const mock = resolveMockAdThumbs(params);
  if (mock) return mock;

  const response = await requestAdsOp({ op: 'ads.thumbs', params });
  const data = readOpData(response.data, 'fetchAdThumbs');
  const raw = 'thumbs' in data && Array.isArray(data.thumbs) ? data.thumbs : [];
  const thumbs: AdThumb[] = [];
  for (const item of raw) {
    const row = (item != null && typeof item === 'object' ? item : {}) as Record<string, unknown>;
    const adId = String(row.adId ?? '').trim();
    const image = String(row.image ?? '').trim();
    if (adId && image) thumbs.push({ adId, image });
  }
  return { thumbs };
}
